/**
 * Adapter: PL `Review` (from REVIEWS_LIST) → the non-DS `ProductReviewHighLight` card props.
 *
 * Mirrors the legacy mapping in `ReviewHighlights.tsx` (the non-PL highlights organism), with the
 * one proto rename: the review body is `text` here (was `body` on the legacy domain).
 */

import type { ImageProps } from '@src/quince-components/src/atoms/image'
import type { ProductReviewHighLightProps } from '@src/quince-components/src/atoms/productReviewHighlight'
import type { FillRangeItemProps } from '@src/quince-components/src/atoms/reviews/fillRangeItem'
import type { ReviewItemProps } from '@src/quince-components/src/atoms/reviews/reviewItem'
import Image from '@src/quince-site-common/src/domains/Image'
import type Product from '@src/quince-site-common/src/domains/Product'
import type { IProduct } from '@src/quince-site-common/src/domains/Product'
import ProductImage from '@src/quince-site-common/src/domains/ProductImage'
import ProductReview, { ReviewOptionType } from '@src/quince-site-common/src/domains/ProductReview'

import type { Review, ReviewsHighlightOption } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const EXTERNAL_BRAND_NAME = 'EXTERNAL_BRAND_NAME'

export const brandNameFromTags = (review: Review): string | undefined =>
  review.reviewTags.find(tag => tag.tags === EXTERNAL_BRAND_NAME)?.tagValues.join(', ')

export const adaptReviewToHighlight = (
  review: Review,
  regionIdToDisplayName: Record<number, string>,
  rewardProgrammeLearnMoreUrl?: string,
): ProductReviewHighLightProps => ({
  avgRating: review.rating,
  title: review.title ?? '',
  // PL proto carries the body in `text`; the card renders it via `children`.
  children: review.text ?? '',
  // Third-party attribution: the card shows "Customer review from {brandName}" when present.
  brandName: brandNameFromTags(review),
  // regionId → country display name (locale map); ReviewMetaRow renders it under the locale flag.
  reviewedInCountry: review.regionId ? regionIdToDisplayName[review.regionId] : '',
  isIncentivized: review.isIncentivized,
  rewardProgrammeLearnMoreUrl,
})

const REVIEW_MEDIA_IMAGE = 'IMAGE'

/**
 * Full review card mapping for the REVIEWS_FULL surface — `Review` → the non-DS `ReviewItem`.
 * Unlike the condensed highlight card, this renders author, date, the attribute sliders/dropdowns,
 * and review photos.
 */
export const adaptReviewToReviewItem = (
  review: Review,
  index: number,
  regionIdToDisplayName: Record<number, string>,
  rewardProgrammeLearnMoreUrl?: string,
): ReviewItemProps => ({
  reviewId: review.id,
  index,
  author: review.author ?? '',
  // proto `createdDate` is epoch SECONDS (float); ReviewItem's DateTime formats a date value.
  date: review.createdDate ? new Date(review.createdDate * 1000).toISOString() : '',
  rating: review.rating,
  title: review.title ?? '',
  body: review.text ?? '',
  // `attributeType` values ('slider' | 'dropdown') already match ReviewOptionType; ReviewItem
  // splits PRODUCT_PROFILE (sliders) vs CUSTOMER_PROFILE (size/typical-size) internally.
  // IReviewOption requires attributeType/attributeName/translatedAttributeName/displayName/
  // translatedDisplayName/value (percentage optional); the proto marks these optional, so default
  // to '' — `value` is the slider notch ("4") or dropdown choice ("M").
  reviewOptions: review.reviewAttributes.map(attr => ({
    attributeType: (attr.attributeType ?? ReviewOptionType.PRODUCT_PROFILE) as ReviewOptionType,
    attributeName: attr.attributeName ?? '',
    translatedAttributeName: attr.translatedAttributeName ?? '',
    displayName: attr.displayName ?? '',
    translatedDisplayName: attr.translatedDisplayName ?? '',
    percentage: attr.percentage,
    value: attr.value ?? '',
  })),
  reviewImageProps: review.reviewMedias
    .filter(media => (media.mediaType ?? '').toUpperCase() === REVIEW_MEDIA_IMAGE && media.mediaUrl)
    .map((media, i): ImageProps => ({ id: `${review.id}-${i}`, url: media.mediaUrl ?? '', title: '', alt: '' })),
  source: review.source,
  brandName: brandNameFromTags(review),
  reviewedInCountry: review.regionId ? regionIdToDisplayName[review.regionId] : undefined,
  isIncentivized: review.isIncentivized,
  // Not on the proto `Review` yet (needs a storefront-proto-registry bump once the BE ships it);
  // read defensively so the badge lights up as soon as the BFF forwards the field.
  verifiedPurchase: (review as Review & { verifiedPurchase?: boolean }).verifiedPurchase,
  rewardProgrammeLearnMoreUrl,
})

/**
 * Gallery mapping — `Review` → the `ProductReview` shape the non-DS `ProductReviewGallerySlider`
 * reads. The slider itself only needs `id` / `title` / `imagesFileUrls`, but the shared
 * `ReviewPopUp` (opened on image click) flattens these same reviews into its slides and reads
 * `author` / `rating` / `createdAt` / `title` / `body` — so populate them here too, otherwise the
 * popup slides render a blank author/rating/date/body. Flattens `reviewMedias` (IMAGE only) to the
 * `imagesFileUrls` string[] the slider iterates.
 */
/**
 * Build the minimal product the legacy write-a-review popup (`CreateReview`) needs, from the PL
 * layout data (product id + title) — NO enrich/product-service call.
 *
 * `CreateReview` types its `product` as the full `IProduct` (~50 fields: contentfulProductId, gender,
 * videos, vendorCodes, productRecommendations, …) but only reads `id` (submit `quinceProductId` + the
 * questionnaire fetch), `title` (header), and empty `images`/`variants`/`dynamicImages`/`productOptions`
 * (the option & image helpers traverse these). We can't edit that shared prop type. We build exactly
 * the consumed fields as a `Partial<IProduct>` and narrow with a single `as Product` downcast (a
 * normal narrowing — NOT an `as unknown as` double cast; `Product` is assignable to `Partial<IProduct>`,
 * so the assertion is valid).
 *
 * `imageUrl` (the SEO image from `meta.seoData.imageUrl`) becomes the popup header thumbnail: the popup
 * resolves it via `getImagesForOptions(product)[0].image.url`, so we wrap it in a single real
 * `ProductImage`. Empty/absent → no thumbnail. `variants` can be enriched later when the BE adds it.
 */
export const buildWriteReviewProduct = (productId: string, title: string, imageUrl?: string): Product => {
  const product: Partial<IProduct> = {
    id: isNaN(Number(productId)) ? productId : Number(productId),
    spid: productId,
    title,
    images: imageUrl
      ? [
          new ProductImage({
            image: new Image({ id: '', url: imageUrl, fileName: '', height: 0, width: 0, displayModelInfo: false }),
            options: [],
          }),
        ]
      : [],
    variants: [],
    dynamicImages: [],
    productOptions: [],
  }
  return product as Product
}

export const adaptReviewToGallery = (review: Review): ProductReview =>
  new ProductReview({
    id: review.id,
    title: review.title ?? '',
    author: review.author ?? '',
    rating: review.rating,
    productId: String(review.productId ?? ''),
    // Not carried on the gallery Review proto; the slider/popup don't render it.
    productTitle: '',
    // proto `createdDate` is epoch SECONDS; ReviewPopUp does `new Date(createdAt)`.
    createdAt: review.createdDate ? new Date(review.createdDate * 1000).toISOString() : '',
    body: review.text ?? '',
    imagesFileUrls: review.reviewMedias
      .filter(media => (media.mediaType ?? '').toUpperCase() === REVIEW_MEDIA_IMAGE && media.mediaUrl)
      .map(media => media.mediaUrl as string),
  })

/**
 * Adapter: PL `ReviewsHighlightOption[]` (the REVIEWS_HIGHLIGHT proto's rating-range `options`) →
 * the DS `FillRangeItemProps[]` the `ProductReviewSummaryDS` info-sliders read. Same mapping the
 * legacy FULL branch does inline, with the DS field names (`title`/`fillPercentage`/`minLabel`/
 * `maxLabel`).
 */
export const adaptReviewOptionsToRanges = (options: ReviewsHighlightOption[]): FillRangeItemProps[] =>
  options.map(option => ({
    title: option.name,
    fillPercentage: option.percent,
    minLabel: option.options[0] ?? '',
    maxLabel: option.options[1] ?? '',
  }))
